import { computed, shallowRef } from 'vue'
import { readSigningCertificate } from '@/helpers/pdfSigning/certificateStore'
import type { SigningCredential, SigningCredentialErrorCode } from '@/helpers/pdfSigning/p12'
import type { User } from '@/models/User/Model'

// The signing credential unlocked in this tab (T17.2): the signed-in user's stored P12/PFX opened
// with its password by unlockP12, as a non-extractable Web Crypto key that can only sign, with its
// certificate. It lives only in this tab's memory, for the user and the exact file (SHA-256) it
// was opened from. A reload or closing the tab forgets it; so does lock(), which runs when the
// signed-in user changes or signs out (useAuth), when the certificate is replaced or removed
// (useSigningCertificate), and when another tab does either for the same user. The password is
// used once to open the file and kept nowhere; nothing here is stored, logged or sent.

interface Unlocked {
	userId: User['id']
	sha256: string
	credential: SigningCredential
}

const unlocked = shallowRef<Unlocked | null>(null)
// Every lock starts a new generation. An unlock opened in an older one, finishing after a sign-out,
// a removal or a replacement, is discarded instead of reviving a key for what was locked.
let generation = 0
// The users with an unlock in progress in this tab, and how many.
const pending = new Map<User['id'], number>()

// Other tabs of this origin lock too when a user's certificate changes or the user signs out. The
// message is the user ID only. It locks this tab's credential for that user and also discards an
// unlock of that user still in progress, so it cannot finish into a key afterwards.
const channel =
	typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('racuni-signing')
channel?.addEventListener('message', (event: MessageEvent<{ userId?: User['id'] }>) => {
	const userId = event.data?.userId
	if (userId === undefined) return
	if (unlocked.value?.userId === userId || pending.has(userId)) lockHere()
})

export type UnlockProblem =
	| SigningCredentialErrorCode
	// The stored certificate is not the one shown any more, or is gone.
	| 'certificate-changed'
	// Locked meanwhile: signed out, another user, or the certificate replaced or removed.
	| 'locked-meanwhile'

export class UnlockError extends Error {
	constructor(readonly problem: UnlockProblem) {
		super(problem)
	}
}

/** Who the unlocked certificate belongs to, for display; null while locked. */
export const unlockedSigner = computed(() =>
	unlocked.value
		? {
				userId: unlocked.value.userId,
				sha256: unlocked.value.sha256,
				holderName: unlocked.value.credential.holderName,
			}
		: null,
)

/**
 * Opens the user's stored certificate, the one with this SHA-256, with the password, and keeps it
 * unlocked in this tab. Throws UnlockError: the unlockP12 codes such as 'wrong-password', or
 * 'certificate-changed', or 'locked-meanwhile' when a lock came first.
 */
export async function unlock(userId: User['id'], sha256: string, password: string): Promise<void> {
	const started = generation
	pending.set(userId, (pending.get(userId) ?? 0) + 1)
	try {
		const record = await readSigningCertificate(userId)
		if (!record || record.sha256 !== sha256) throw new UnlockError('certificate-changed')
		// PKIjs loads only when a certificate is unlocked or imported.
		const { unlockP12, SigningCredentialError } = await import('@/helpers/pdfSigning/p12')
		let credential: SigningCredential
		try {
			credential = await unlockP12(new Uint8Array(record.bundle), password)
		} catch (error) {
			if (error instanceof SigningCredentialError) throw new UnlockError(error.code)
			throw error
		}
		if (generation !== started) throw new UnlockError('locked-meanwhile')
		unlocked.value = { userId, sha256, credential }
	} finally {
		const count = pending.get(userId) ?? 1
		if (count > 1) pending.set(userId, count - 1)
		else pending.delete(userId)
	}
}

/**
 * The credential unlocked in this tab for this user and this stored file, or null. Signing (T17.3)
 * reads it here and nowhere else.
 */
export function unlockedCredential(userId: User['id'], sha256: string): SigningCredential | null {
	const current = unlocked.value
	return current && current.userId === userId && current.sha256 === sha256
		? current.credential
		: null
}

/**
 * Forgets the unlocked credential in this tab and discards unlocks in progress. With a user ID,
 * other tabs forget theirs for that user too.
 */
export function lock(userId?: User['id'] | null): void {
	lockHere()
	if (userId !== undefined && userId !== null) channel?.postMessage({ userId })
}

function lockHere() {
	generation++
	unlocked.value = null
}
